import {
  makeUserBuildingAdmin,
  toggleAccess,
  toggleBooking,
  toggleBookingVisibleForUsers,
  toggleCommsForBuildingAdmins,
  toggleCommsForUsers,
  toggleDashboardEnabledForAdmins,
  toggleDashboardEnabledForUsers,
  toggleMillum,
  toggleMunu,
  updateBuildingSettings,
  useBuilding,
  toggleParking,
  companiesNamesOptions,
  toggleSnack,
  toggleSnackForUsers,
  createNewBuilding,
  useAllBuildings,
  toggleQuarterlyUserPurge,
  toggleWelcomeFood,
  toggleFoodWalletRefund,
  runQuarterlyUserPurgeManually,
  runTwoWeekNoticeManually,
  runOneMonthNoticeManually,
  syncDefigoUsersManually,
  syncCardsInArxManually,
  syncStarwatchAccessGroupsManually,
} from "@/api";
import { useWelcomeSuperAccess } from "@/hooks/useWelcomeSuperAccess";
import {
  Toggle,
  InputField,
  Button,
  Dropdown,
  Icon,
  LoadingDots,
  PopupCard,
} from "@app-components";
import { useQueryClient } from "@tanstack/react-query";
import { Link, createFileRoute } from "@tanstack/react-router";
import { CSSProperties, useEffect, useMemo, useRef, useState } from "react";
import { createBookingBuilding, useBookingBuilding } from "@/api";
import { getBuildingId } from "@/lib";
import { REAL_ESTATE } from "@/lib";
import { useLoggedInUser, useSnackbarStore } from "@/store/zustandStore";
import { SelectOption } from "react-spreadsheet-import/types/types";
import { useBuildingQuery } from "@/components/RouterSetup";
import { AccessSystem, Building, NotificationType } from "@/types";
import { TwoNIntercomCard } from "@/components/building_admin/TwoNIntercomCard";

export const Route = createFileRoute("/_portal/building/supersettings/")({
  component: RouteComponent,
});

type BookingFieldDef = {
  key:
    | "buildingId"
    | "buildingName"
    | "description"
    | "floors"
    | "floorNames"
    | "addressLines"
    | "regionCode"
    | "postalCode"
    | "administrativeArea"
    | "locality";
  label: string;
  type: string;
  placeholder: string;
  readOnly?: boolean;
  help?: string;
  required?: boolean;
};

// Order in which we look for the first empty field to focus when opening
// the setup modal for a first-time setup.
const BOOKING_SETUP_EDITABLE_KEYS = [
  "buildingName",
  "description",
  "floors",
  "floorNames",
  "addressLines",
  "postalCode",
  "locality",
] as const;

const BOOKING_FIELD_GROUPS: ReadonlyArray<{
  section: string;
  fields: ReadonlyArray<BookingFieldDef>;
}> = [
  {
    section: "Building",
    fields: [
      {
        key: "buildingId",
        label: "Building ID",
        type: "text",
        readOnly: true,
        placeholder: "",
        help: "Auto-filled from the current building.",
      },
      {
        key: "buildingName",
        label: "Building name",
        type: "text",
        placeholder: "e.g. Skippergata 3",
        required: true,
      },
      {
        key: "description",
        label: "Description",
        type: "text",
        placeholder: "e.g. This building belongs to Entra",
        required: true,
      },
      {
        key: "floors",
        label: "Number of floors",
        type: "number",
        placeholder: "e.g. 3",
        required: true,
      },
      {
        key: "floorNames",
        label: "Floor names",
        type: "text",
        placeholder: "e.g. 1, 2, 3",
        help: "Comma-separated.",
      },
    ],
  },
  {
    section: "Address",
    fields: [
      {
        key: "addressLines",
        label: "Address",
        type: "text",
        placeholder: "e.g. Skippergata 3, Oslo",
        required: true,
      },
      {
        key: "postalCode",
        label: "Postal code",
        type: "text",
        placeholder: "e.g. 0152",
        required: true,
      },
      {
        key: "locality",
        label: "City",
        type: "text",
        placeholder: "e.g. Oslo",
        required: true,
      },
      {
        key: "regionCode",
        label: "Country code",
        type: "text",
        readOnly: true,
        placeholder: "",
      },
      {
        key: "administrativeArea",
        label: "Region",
        type: "text",
        readOnly: true,
        placeholder: "",
      },
    ],
  },
];

const isPositiveIntFloors = (floors: string) =>
  /^[1-9]\d*$/.test(floors.trim());

const isBookingSetupFormComplete = (form: {
  buildingName: string;
  description: string;
  floors: string;
  floorNames: string;
  addressLines: string;
  postalCode: string;
  locality: string;
}) =>
  BOOKING_SETUP_EDITABLE_KEYS.every((key) =>
    key === "floors"
      ? isPositiveIntFloors(form.floors)
      : String(form[key]).trim() !== "",
  );

const cardStyle = {
  padding: "var(--wlcm-spacing-sm)",
  background: "var(--wlcm-color-inactive-grey)",
  borderRadius: "var(--wlcm-border-radius-sm)",
  flex: 1,
  width: "50%",
  minWidth: 380,
  display: "flex",
  flexDirection: "column" as CSSProperties["flexDirection"],
  gap: "var(--wlcm-spacing-xs)",
};

const iconStyle = {
  verticalAlign: "text-top",
  marginRight: "var(--wlcm-spacing-xs)",
};

function RouteComponent() {
  const isWelcomeWorkdaysUser = useWelcomeSuperAccess();
  const { data: building } = useBuilding();
  const queryClient = useQueryClient();
  const loggedInUser = useLoggedInUser();
  const { data: allBuildings } = useAllBuildings();
  const { addNotification } = useSnackbarStore();
  const [isTogglingCommsForAdmins, setIsTogglingCommsForAdmins] =
    useState(false);
  const [isTogglingCommsForUsers, setIsTogglingCommsForUsers] = useState(false);
  const [isTogglingWelcomeFood, setIsTogglingWelcomeFood] = useState(false);
  const [foodRetryTarget, setFoodRetryTarget] = useState<boolean | null>(null);
  const [snackRetryTarget, setSnackRetryTarget] = useState<boolean | null>(
    null,
  );
  const [isTogglingFoodWalletRefund, setIsTogglingFoodWalletRefund] =
    useState(false);
  const [passentryTemplateId, setPassentryTemplateId] = useState(
    building.passentryTemplateId ?? "",
  );
  const [lastSyncedPassentryTemplateId, setLastSyncedPassentryTemplateId] =
    useState(building.passentryTemplateId ?? "");

  useEffect(() => {
    const next = building.passentryTemplateId ?? "";

    // Only overwrite local edits when the user hasn't changed the field since the last sync.
    setPassentryTemplateId((prev) =>
      prev === lastSyncedPassentryTemplateId ? next : prev,
    );
    setLastSyncedPassentryTemplateId(next);
  }, [building.passentryTemplateId, lastSyncedPassentryTemplateId]);
  const [isTogglingMunu, setIsTogglingMunu] = useState(false);
  const [isTogglingMillum, setIsTogglingMillum] = useState(false);
  const [millumGLN, setMillumGLN] = useState(building.millumGLN ?? "");
  const [defigoBuildingId, setDefigoBuildingId] = useState(
    building.defigoBuildingId ?? "",
  );
  const [isTogglingDefigo, setIsTogglingDefigo] = useState(false);
  const [isRunningDefigoSync, setIsRunningDefigoSync] = useState(false);
  const [isTogglingBooking, setIsTogglingBooking] = useState(false);
  const [
    isTogglingBookingVisibleForUsers,
    setIsTogglingBookingVisibleForUsers,
  ] = useState(false);
  const [isTogglingAccess, setIsTogglingAccess] = useState(false);
  const [isRunningCardsInArxSync, setIsRunningCardsInArxSync] = useState(false);
  const [isRunningStarwatchGroupSync, setIsRunningStarwatchGroupSync] =
    useState(false);
  const [
    isTogglingBuildingDigitalBadgeMode,
    setIsTogglingBuildingDigitalBadgeMode,
  ] = useState(false);
  const [
    isTogglingDashboardEnabledForAdmins,
    setIsTogglingDashboardEnabledForAdmins,
  ] = useState(false);
  const [
    isTogglingDashboardEnabledForUsers,
    setIsTogglingDashboardEnabledForUsers,
  ] = useState(false);
  const [isTogglingAllowBadgePrinting, setIsTogglingAllowBadgePrinting] =
    useState(false);
  const [isTogglingParking, setIsTogglingParking] = useState(false);
  const [handbookLink, setHandbooklink] = useState(building.handbookLink);
  const [isUpdatingHandbookLink, setIsUpdatingHandbookLink] = useState(false);
  const [isTogglingSnackForUsers, setIsTogglingSnackForUsers] = useState(false);
  const [isTogglingSnackForAdmins, setIsTogglingSnackForAdmins] =
    useState(false);
  const [isTogglingQuarterlyUserPurge, setIsTogglingQuarterlyUserPurge] =
    useState(false);
  const [
    isTogglingAllowBuildingAdminPinManagement,
    setIsTogglingAllowBuildingAdminPinManagement,
  ] = useState(false);
  const [
    isTogglingAllowAccessRoleManagement,
    setIsTogglingAllowAccessRoleManagement,
  ] = useState(false);
  const [
    isTogglingForgotCardCodeGeneration,
    setIsTogglingForgotCardCodeGeneration,
  ] = useState(false);
  const [
    isTogglingVisitorAccessCodeViaPoly,
    setIsTogglingVisitorAccessCodeViaPoly,
  ] = useState(false);
  const [isTogglingWelcomeVisit, setIsTogglingWelcomeVisit] = useState(false);
  const [isTogglingVisitorLabelPrinting, setIsTogglingVisitorLabelPrinting] =
    useState(false);
  const [isTogglingAcsConnectivityLost, setIsTogglingAcsConnectivityLost] =
    useState(false);
  const [acsConnectivityLostMessageNo, setAcsConnectivityLostMessageNo] =
    useState(building.acsConnectivityLostMessage?.no ?? "");
  const [acsConnectivityLostMessageEn, setAcsConnectivityLostMessageEn] =
    useState(building.acsConnectivityLostMessage?.en ?? "");
  const [showAcsMessageFields, setShowAcsMessageFields] = useState(
    building.acsConnectivityLost ?? false,
  );
  const [newBuildingId, setNewBuildingId] = useState("");
  const [isCreatingNewBuilding, setIsCreatingNewBuilding] = useState(false);
  const [isMakingUserBuildingAdmin, setIsMakingUserBuildingAdmin] =
    useState(false);
  const [bookingBuilding, setBookingBuilding] = useState({
    buildingId: getBuildingId() || REAL_ESTATE,
    buildingName: building.displayName || "",
    description: "",
    floors: "",
    floorNames: "",
    addressLines: "",
    regionCode: "NO",
    postalCode: "",
    administrativeArea: "Norway",
    locality: "",
  });
  const [isCreatingBookingBuilding, setIsCreatingBookingBuilding] =
    useState(false);
  const [bookingBuildingCreated, setBookingBuildingCreated] = useState(false);
  const {
    data: bookingBuildingResponse,
    isPending: isBookingStatusLoading,
    isError: isBookingStatusError,
  } = useBookingBuilding(!!building.bookingEnabled);
  const isBookingStatusKnown = !isBookingStatusLoading && !isBookingStatusError;
  const isBookingConfigured =
    !!bookingBuildingResponse || bookingBuildingCreated;
  const [showBookingSetupModal, setShowBookingSetupModal] = useState(false);
  // Set when the user enables booking while the config-status query is still
  // in flight, so we can auto-open the setup modal once we actually know
  // whether the building is configured.
  const [pendingSetupModalOnToggle, setPendingSetupModalOnToggle] =
    useState(false);
  const firstEmptyEditableKey = BOOKING_SETUP_EDITABLE_KEYS.find(
    (k) => !bookingBuilding[k],
  );
  useEffect(() => {
    if (!pendingSetupModalOnToggle) return;
    if (!isBookingStatusKnown) return;
    setPendingSetupModalOnToggle(false);
    if (!isBookingConfigured) setShowBookingSetupModal(true);
  }, [pendingSetupModalOnToggle, isBookingStatusKnown, isBookingConfigured]);
  const modalInitialFocusRef = useRef<HTMLInputElement | null>(null);
  useEffect(() => {
    if (!showBookingSetupModal) return;
    // PopupCard activates a focus trap on mount that seizes focus to its
    // close button, overriding React's native autoFocus. Wait one frame
    // for the trap to settle, then move focus to the intended field.
    const raf = requestAnimationFrame(() => {
      modalInitialFocusRef.current?.focus();
    });
    return () => cancelAnimationFrame(raf);
  }, [showBookingSetupModal]);
  const [
    isRunningQuarterlyUserPurgeManually,
    setIsRunningQuarterlyUserPurgeManually,
  ] = useState(false);
  const [isRunningTwoWeekNotice, setIsRunningTwoWeekNotice] = useState(false);
  const [isRunningOneMonthNotice, setIsRunningOneMonthNotice] = useState(false);
  const [isTogglingAcsConnectivityGuard, setIsTogglingAcsConnectivityGuard] =
    useState(false);

  const updateBookingField = (field: string, value: string | number) => {
    setBookingBuilding((prev) => ({ ...prev, [field]: value }));
  };

  const { data: companies } = useBuildingQuery(
    companiesNamesOptions([], {
      getAllIfNoCompanyIds: true,
      noExternal: false,
    }),
  );

  const buildingOwnerOptions = useMemo(() => {
    return [
      "Entra ASA",
      "Storebrand",
      "Malling",
      "Reitan",
      "DNB",
      "KLP",
      "Union DnB",
    ].map((owner) => ({
      label: owner,
      value: owner,
    }));
  }, []);

  const [selectedOwner, setSelectedOwner] = useState<SelectOption | undefined>(
    undefined,
  );

  if (!isWelcomeWorkdaysUser) return null;
  const adminCommsHandler = async () => {
    try {
      setIsTogglingCommsForAdmins(true);
      await toggleCommsForBuildingAdmins();
      await queryClient.invalidateQueries({
        queryKey: [getBuildingId(), "building"],
      });
    } catch (error) {
      console.log("error ", error);
    } finally {
      setIsTogglingCommsForAdmins(false);
    }
  };
  const userCommsHandler = async () => {
    try {
      setIsTogglingCommsForUsers(true);
      await toggleCommsForUsers();
      await queryClient.invalidateQueries({
        queryKey: [getBuildingId(), "building"],
      });
    } catch (error) {
      console.log("error ", error);
    } finally {
      setIsTogglingCommsForUsers(false);
    }
  };
  const welcomeFoodHandler = async (enabled: boolean) => {
    try {
      setIsTogglingWelcomeFood(true);
      setFoodRetryTarget(enabled);
      const wasEnabled = building.hasWelcomeFood ?? false;
      const hadTemplateId = !!building.passentryTemplateId;
      await toggleWelcomeFood(enabled);
      setFoodRetryTarget(null);
      await queryClient.invalidateQueries({
        queryKey: [getBuildingId(), "building"],
      });
      await queryClient.invalidateQueries({
        queryKey: [getBuildingId(), "supplier"],
      });

      if (enabled && !wasEnabled && !hadTemplateId) {
        await queryClient.refetchQueries({
          queryKey: [getBuildingId(), "building"],
        });
        const refreshed = queryClient.getQueryData<Building>([
          getBuildingId(),
          "building",
        ]);
        if (refreshed?.passentryTemplateId) {
          addNotification(
            "Passentry Template ID was auto-populated, and saved.",
            NotificationType.SUCCESS,
          );
        } else {
          addNotification(
            "Could not auto-populate Passentry Template ID — please set it manually",
            NotificationType.WARNING,
          );
        }
      }
    } catch (error) {
      console.log("error ", error);
      addNotification(
        "Welcome Food synchronization did not complete. Retry the requested change.",
        NotificationType.ERROR,
      );
      await queryClient.invalidateQueries({
        queryKey: [getBuildingId(), "building"],
      });
    } finally {
      setIsTogglingWelcomeFood(false);
    }
  };
  const foodWalletRefundHandler = async () => {
    try {
      setIsTogglingFoodWalletRefund(true);
      await toggleFoodWalletRefund();
      await queryClient.invalidateQueries({
        queryKey: [getBuildingId(), "building"],
      });
    } catch (error) {
      console.log("error ", error);
    } finally {
      setIsTogglingFoodWalletRefund(false);
    }
  };
  const munuHandler = async () => {
    try {
      setIsTogglingMunu(true);
      await toggleMunu();
      await queryClient.invalidateQueries({
        queryKey: [getBuildingId(), "building"],
      });
    } catch (error) {
      console.log("error ", error);
    } finally {
      setIsTogglingMunu(false);
    }
  };
  const millumHandler = async () => {
    try {
      setIsTogglingMillum(true);
      await toggleMillum();
      await queryClient.invalidateQueries({
        queryKey: [getBuildingId(), "building"],
      });
    } catch (error) {
      console.log("error ", error);
    } finally {
      setIsTogglingMillum(false);
    }
  };
  const bookingEnabledHandler = async () => {
    try {
      setIsTogglingBooking(true);
      const wasEnabled = building.bookingEnabled ?? false;
      await toggleBooking();
      await queryClient.invalidateQueries({
        queryKey: [getBuildingId(), "building"],
      });
      if (!wasEnabled) {
        if (isBookingStatusKnown) {
          if (!isBookingConfigured) setShowBookingSetupModal(true);
        } else {
          // Status still loading — defer the open until it settles.
          setPendingSetupModalOnToggle(true);
        }
      }
    } catch (error) {
      console.log("error ", error);
    } finally {
      setIsTogglingBooking(false);
    }
  };
  const bookingVisibleForUserHandler = async () => {
    try {
      setIsTogglingBookingVisibleForUsers(true);
      await toggleBookingVisibleForUsers();
      await queryClient.invalidateQueries({
        queryKey: [getBuildingId(), "building"],
      });
    } catch (error) {
      console.log("error ", error);
    } finally {
      setIsTogglingBookingVisibleForUsers(false);
    }
  };
  const accessEnabledHandler = async () => {
    try {
      setIsTogglingAccess(true);
      await toggleAccess();
      await queryClient.invalidateQueries({
        queryKey: [getBuildingId(), "building"],
      });
    } catch (error) {
      console.log("error ", error);
    } finally {
      setIsTogglingAccess(false);
    }
  };
  const handleCardsInArxSync = async (dryRun: boolean) => {
    try {
      setIsRunningCardsInArxSync(true);
      await syncCardsInArxManually(dryRun);
      addNotification(
        dryRun
          ? "Dry-run sync completed successfully"
          : "Sync completed successfully",
        NotificationType.SUCCESS,
      );
    } catch (error) {
      console.error("Cards in ARX sync error:", error);
      addNotification("Cards in ARX sync failed", NotificationType.ERROR);
    } finally {
      setIsRunningCardsInArxSync(false);
    }
  };
  const handleStarwatchGroupSync = async () => {
    try {
      setIsRunningStarwatchGroupSync(true);
      const summary = await syncStarwatchAccessGroupsManually();
      const counts = `Starwatch access groups synced: ${summary.added} added, ${summary.renamed} renamed, ${summary.reactivated} reactivated, ${summary.removed} removed`;
      // One notification: two in a row can get the same snackbar ID
      if (summary.removalsSkipped) {
        addNotification(
          `${counts}. Starwatch returned no access groups, so none were marked as removed`,
          NotificationType.WARNING,
        );
      } else {
        addNotification(counts, NotificationType.SUCCESS);
      }
    } catch (error) {
      console.error("Starwatch access group sync error:", error);
      addNotification(
        "Starwatch access group sync failed",
        NotificationType.ERROR,
      );
    } finally {
      setIsRunningStarwatchGroupSync(false);
    }
  };
  const buildingDigitalBadgeModeHandler = async () => {
    try {
      setIsTogglingBuildingDigitalBadgeMode(true);
      await updateBuildingSettings({
        acsDigitalBadgeMode: !building.acsDigitalBadgeMode,
      });
      await queryClient.invalidateQueries({
        queryKey: [getBuildingId(), "building"],
      });
    } catch (error) {
      console.log("error ", error);
    } finally {
      setIsTogglingBuildingDigitalBadgeMode(false);
    }
  };
  const dashboardEnabledForAdminsHandler = async () => {
    try {
      setIsTogglingDashboardEnabledForAdmins(true);
      await toggleDashboardEnabledForAdmins();
      await queryClient.invalidateQueries({
        queryKey: [getBuildingId(), "building"],
      });
    } catch (error) {
      console.log("error ", error);
    } finally {
      setIsTogglingDashboardEnabledForAdmins(false);
    }
  };
  const dashboardEnabledForUsersHandler = async () => {
    try {
      setIsTogglingDashboardEnabledForUsers(true);
      await toggleDashboardEnabledForUsers();
      await queryClient.invalidateQueries({
        queryKey: [getBuildingId(), "building"],
      });
    } catch (error) {
      console.log("error ", error);
    } finally {
      setIsTogglingDashboardEnabledForUsers(false);
    }
  };
  const snackEnabledForAdminsHandler = async (enabled: boolean) => {
    try {
      setIsTogglingSnackForAdmins(true);
      setSnackRetryTarget(enabled);
      await toggleSnack(enabled);
      setSnackRetryTarget(null);
      await queryClient.invalidateQueries({
        queryKey: [getBuildingId(), "building"],
      });
      await queryClient.invalidateQueries({
        queryKey: [getBuildingId(), "supplier"],
      });
    } catch (error) {
      console.log("error ", error);
      addNotification(
        "Snack synchronization did not complete. Retry the requested change.",
        NotificationType.ERROR,
      );
      await queryClient.invalidateQueries({
        queryKey: [getBuildingId(), "building"],
      });
    } finally {
      setIsTogglingSnackForAdmins(false);
    }
  };
  const snackEnabledForUsersHandler = async () => {
    try {
      setIsTogglingSnackForUsers(true);
      await toggleSnackForUsers();
      await queryClient.invalidateQueries({
        queryKey: [getBuildingId(), "building"],
      });
    } catch (error) {
      console.log("error ", error);
    } finally {
      setIsTogglingSnackForUsers(false);
    }
  };

  const savePassentryTemplateId = async () => {
    setIsTogglingWelcomeFood(true);
    await updateBuildingSettings({ passentryTemplateId });
    await queryClient.invalidateQueries({
      queryKey: [getBuildingId(), "building"],
    });
    setIsTogglingWelcomeFood(false);
  };

  const saveMillumGLN = async () => {
    setIsTogglingMillum(true);
    await updateBuildingSettings({ millumGLN });
    await queryClient.invalidateQueries({
      queryKey: [getBuildingId(), "building"],
    });
    setIsTogglingMillum(false);
  };

  const saveDefigoBuildingId = async () => {
    setIsTogglingDefigo(true);
    await updateBuildingSettings({ defigoBuildingId });
    await queryClient.invalidateQueries({
      queryKey: [getBuildingId(), "building"],
    });
    setIsTogglingDefigo(false);
  };

  const saveHandbookLink = async () => {
    setIsUpdatingHandbookLink(true);
    await updateBuildingSettings({ handbookLink });
    await queryClient.invalidateQueries({
      queryKey: [getBuildingId(), "building"],
    });
    setIsUpdatingHandbookLink(false);
  };

  const allowBadgePrintingHandler = async () => {
    try {
      setIsTogglingAllowBadgePrinting(true);
      await updateBuildingSettings({
        allowBadgePrinting: !building.allowBadgePrinting,
      });
      await queryClient.invalidateQueries({
        queryKey: [getBuildingId(), "building"],
      });
    } catch (error) {
      console.log("error ", error);
    } finally {
      setIsTogglingAllowBadgePrinting(false);
    }
  };

  const parkingHandler = async () => {
    try {
      setIsTogglingParking(true);
      await toggleParking();
      await queryClient.invalidateQueries({
        queryKey: [getBuildingId(), "building"],
      });
    } catch (error) {
      console.log("error ", error);
    } finally {
      setIsTogglingParking(false);
    }
  };

  const quarterlyUserPurgeHandler = async () => {
    try {
      setIsTogglingQuarterlyUserPurge(true);
      await toggleQuarterlyUserPurge();
      await queryClient.invalidateQueries({
        queryKey: [getBuildingId(), "building"],
      });
    } catch (error) {
      console.log("error ", error);
    } finally {
      setIsTogglingQuarterlyUserPurge(false);
    }
  };

  const forgotCardCodeGenerationHandler = async () => {
    try {
      setIsTogglingForgotCardCodeGeneration(true);
      await updateBuildingSettings({
        acsAllowForgotCardCodeGeneration:
          !building.acsAllowForgotCardCodeGeneration,
      });
      await queryClient.invalidateQueries({
        queryKey: [getBuildingId(), "building"],
      });
    } catch (error) {
      console.log("error ", error);
    } finally {
      setIsTogglingForgotCardCodeGeneration(false);
    }
  };
  const visitorAccessCodeViaPolyHandler = async () => {
    try {
      setIsTogglingVisitorAccessCodeViaPoly(true);
      await updateBuildingSettings({
        acsVisitorAccessCodeViaPoly: !building.acsVisitorAccessCodeViaPoly,
      });
      await queryClient.invalidateQueries({
        queryKey: [getBuildingId(), "building"],
      });
    } catch (error) {
      console.log("error ", error);
    } finally {
      setIsTogglingVisitorAccessCodeViaPoly(false);
    }
  };
  const allowBuildingAdminPinManagementHandler = async () => {
    try {
      setIsTogglingAllowBuildingAdminPinManagement(true);
      await updateBuildingSettings({
        allowBuildingAdminPinManagement:
          !building.allowBuildingAdminPinManagement,
      });
      await queryClient.invalidateQueries({
        queryKey: [getBuildingId(), "building"],
      });
    } catch (error) {
      console.log("error ", error);
    } finally {
      setIsTogglingAllowBuildingAdminPinManagement(false);
    }
  };

  const allowAccessRoleManagementHandler = async () => {
    try {
      setIsTogglingAllowAccessRoleManagement(true);
      await updateBuildingSettings({
        allowAccessRoleManagement: !building.allowAccessRoleManagement,
      });
      await queryClient.invalidateQueries({
        queryKey: [getBuildingId(), "building"],
      });
    } catch (error) {
      console.log("error ", error);
    } finally {
      setIsTogglingAllowAccessRoleManagement(false);
    }
  };

  const handleDefigoSync = async (dryRun: boolean) => {
    try {
      setIsRunningDefigoSync(true);
      await syncDefigoUsersManually(dryRun);
      addNotification(
        dryRun
          ? "Dry-run sync completed successfully"
          : "Sync completed successfully",
        NotificationType.SUCCESS,
      );
    } catch (error) {
      console.error("Defigo sync error:", error);
      addNotification("Defigo sync failed", NotificationType.ERROR);
    } finally {
      setIsRunningDefigoSync(false);
    }
  };

  const welcomeVisitHandler = async () => {
    try {
      setIsTogglingWelcomeVisit(true);
      await updateBuildingSettings({
        enableWelcomeVisit: !building.enableWelcomeVisit,
      });
      await queryClient.invalidateQueries({
        queryKey: [getBuildingId(), "building"],
      });
    } catch (error) {
      console.log("error ", error);
    } finally {
      setIsTogglingWelcomeVisit(false);
    }
  };

  const visitorLabelPrintingHandler = async () => {
    try {
      setIsTogglingVisitorLabelPrinting(true);
      await updateBuildingSettings({
        visitorLabelPrintingEnabled: !building.visitorLabelPrintingEnabled,
      });
      await queryClient.invalidateQueries({
        queryKey: [getBuildingId(), "building"],
      });
    } catch (error) {
      console.log("error ", error);
    } finally {
      setIsTogglingVisitorLabelPrinting(false);
    }
  };

  const acsConnectivityHandler = async () => {
    try {
      setIsTogglingAcsConnectivityLost(true);
      const newValue = !building.acsConnectivityLost;
      setShowAcsMessageFields(newValue);
      if (!newValue) {
        setAcsConnectivityLostMessageNo("");
        setAcsConnectivityLostMessageEn("");
      }
      await updateBuildingSettings({
        acsConnectivityLost: newValue,
      });
      await queryClient.invalidateQueries({
        queryKey: [getBuildingId(), "building"],
      });
    } catch (error) {
      console.log("error ", error);
    } finally {
      setIsTogglingAcsConnectivityLost(false);
    }
  };

  const saveAcsConnectivityMessage = async () => {
    try {
      setIsTogglingAcsConnectivityLost(true);
      await updateBuildingSettings({
        acsConnectivityLostMessage: {
          no: acsConnectivityLostMessageNo,
          en: acsConnectivityLostMessageEn,
        },
      });
      await queryClient.invalidateQueries({
        queryKey: [getBuildingId(), "building"],
      });
    } catch (error) {
      console.log("error ", error);
    } finally {
      setIsTogglingAcsConnectivityLost(false);
    }
  };

  const acsConnectivityGuardHandler = async () => {
    try {
      setIsTogglingAcsConnectivityGuard(true);
      await updateBuildingSettings({
        acsConnectivityGuardEnabled: !building.acsConnectivityGuardEnabled,
      });
      await queryClient.invalidateQueries({
        queryKey: [getBuildingId(), "building"],
      });
    } catch (error) {
      console.log("error ", error);
    } finally {
      setIsTogglingAcsConnectivityGuard(false);
    }
  };

  return (
    <div>
      <h1 style={{ marginBottom: "var(--wlcm-spacing-md)" }}>
        Super secret admin page
      </h1>
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: "var(--wlcm-spacing-sm)",
        }}
      >
        <div style={cardStyle}>
          <h2>
            <Icon name="campaign" style={iconStyle} /> Welcome Communication
          </h2>
          <Toggle
            label="Communication enabled for building admins"
            checked={building.hasCommunication ?? false}
            onChange={adminCommsHandler}
            disabled={isTogglingCommsForAdmins}
          />
          <Toggle
            label="Communication enabled for all users"
            checked={building.communicationVisibleForUsers ?? false}
            onChange={userCommsHandler}
            disabled={isTogglingCommsForUsers}
          />
        </div>
        <div style={cardStyle}>
          <h2>
            <Icon name="burger" style={iconStyle} /> Welcome Food
          </h2>
          <Toggle
            label="Welcome Food enabled for building"
            checked={building.hasWelcomeFood ?? false}
            onChange={() => welcomeFoodHandler(!building.hasWelcomeFood)}
            disabled={isTogglingWelcomeFood || foodRetryTarget !== null}
          />
          {foodRetryTarget !== null && (
            <Button
              disabled={isTogglingWelcomeFood}
              onClick={() => welcomeFoodHandler(foodRetryTarget)}
            >
              Retry {foodRetryTarget ? "enabling" : "disabling"} Welcome Food
            </Button>
          )}
          <Toggle
            label="Food wallet refunds enabled"
            checked={building.foodWalletRefundEnabled ?? false}
            onChange={foodWalletRefundHandler}
            disabled={
              isTogglingFoodWalletRefund || !(building.hasWelcomeFood ?? false)
            }
          />
          <InputField
            compact
            label="Passentry Template ID"
            placeholder="Passentry Template ID"
            value={passentryTemplateId}
            disabled={isTogglingWelcomeFood}
            onChange={(e) => {
              setPassentryTemplateId(e.target.value);
            }}
            append={
              <Button
                circular
                icon="save"
                onClick={savePassentryTemplateId}
                style={{ marginRight: -8 }}
              />
            }
          />
          <hr style={{ margin: "var(--wlcm-spacing-sm)", opacity: 0.5 }} />
          <Toggle
            label="Snack enabled for building"
            checked={building.hasWelcomeSnack ?? false}
            onChange={() =>
              snackEnabledForAdminsHandler(!building.hasWelcomeSnack)
            }
            disabled={isTogglingSnackForAdmins || snackRetryTarget !== null}
          />
          {snackRetryTarget !== null && (
            <Button
              disabled={isTogglingSnackForAdmins}
              onClick={() => snackEnabledForAdminsHandler(snackRetryTarget)}
            >
              Retry {snackRetryTarget ? "enabling" : "disabling"} Snack
            </Button>
          )}
          <Toggle
            label="Snack visible for regular users"
            checked={
              (building.hasWelcomeSnack && building.snackVisibleForUsers) ??
              false
            }
            onChange={snackEnabledForUsersHandler}
            disabled={isTogglingSnackForUsers}
          />
          <h4>
            Companies blocked from using Snack{" "}
            <span
              style={{
                color: "var(--wlcm-color-danger-red)",
                paddingLeft: "var(--wlcm-spacing-sm)",
              }}
            >
              ({building.snackBlockedForCompanies?.length ?? 0} /{" "}
              {companies?.length ?? 0})
            </span>
          </h4>
          <div
            style={{
              maxHeight: "200px",
              overflowY: "scroll",
            }}
          >
            <ul style={{ padding: "0" }}>
              {companies?.map((company, index) => (
                <li
                  key={company?.buildingTenantId ?? `partial-${index}`}
                  style={{
                    display: "flex",
                    margin: "5px 0",
                    maxWidth: "250px",
                  }}
                >
                  <span style={{ flex: 1 }}>{company.buildingTenantName}</span>
                  <Toggle
                    red
                    checked={building.snackBlockedForCompanies?.includes(
                      company?.buildingTenantId || "",
                    )}
                    disabled={!building.hasWelcomeSnack}
                    onChange={async (checked) => {
                      try {
                        const newBlockedList = (
                          checked
                            ? Array.from(
                                new Set([
                                  ...(building.snackBlockedForCompanies ?? []),
                                  company?.buildingTenantId,
                                ]),
                              )
                            : (building.snackBlockedForCompanies ?? []).filter(
                                (id) => id !== company?.buildingTenantId,
                              )
                        ).filter((id) => id !== undefined);
                        await updateBuildingSettings({
                          snackBlockedForCompanies: newBlockedList,
                        });
                        await queryClient.invalidateQueries({
                          queryKey: [getBuildingId(), "building"],
                        });
                      } catch (error) {
                        console.error(error);
                      }
                    }}
                  />
                </li>
              ))}
            </ul>
          </div>
          <hr style={{ margin: "var(--wlcm-spacing-sm)", opacity: 0.5 }} />
          <Toggle
            label="Munu"
            checked={building.hasMunu ?? false}
            onChange={munuHandler}
            disabled={isTogglingMunu}
          />
          <div
            style={{ display: "flex", alignItems: "center", flexWrap: "wrap" }}
          >
            <Toggle
              label="Millum"
              checked={building.hasMillum ?? false}
              onChange={millumHandler}
              disabled={isTogglingMillum}
            />
            <InputField
              compact
              hideLabel
              placeholder="Millum GLN"
              value={millumGLN}
              disabled={isTogglingMillum}
              onChange={(e) => {
                setMillumGLN(e.target.value);
              }}
              append={
                <Button
                  circular
                  icon="save"
                  onClick={saveMillumGLN}
                  style={{ marginRight: -8 }}
                />
              }
              style={{ width: "300px", marginLeft: "10px" }}
            />
          </div>
        </div>
        <div style={cardStyle}>
          <h2>
            <Icon name="group" style={iconStyle} /> Welcome Visit
          </h2>
          <Toggle
            label="Welcome Visit enabled for building"
            checked={building.enableWelcomeVisit ?? false}
            onChange={welcomeVisitHandler}
            disabled={isTogglingWelcomeVisit}
          />
          <Toggle
            label="Visitor label printing enabled for building"
            checked={building.visitorLabelPrintingEnabled ?? false}
            onChange={visitorLabelPrintingHandler}
            disabled={isTogglingVisitorLabelPrinting}
          />
        </div>
        <div style={cardStyle}>
          <h2>
            <Icon name="video_camera" style={iconStyle} /> Defigo
          </h2>
          <p
            style={{
              fontSize: "13px",
              color: "var(--wlcm-color-text-secondary)",
              margin: "0 0 8px 0",
            }}
          >
            Sync runs across all buildings, not just this one.
          </p>
          <div
            style={{ display: "flex", alignItems: "center", flexWrap: "wrap" }}
          >
            <InputField
              compact
              hideLabel
              placeholder="Defigo Building ID"
              value={defigoBuildingId}
              disabled={isTogglingDefigo || isRunningDefigoSync}
              onChange={(e) => {
                setDefigoBuildingId(e.target.value);
              }}
              append={
                <Button
                  circular
                  icon="save"
                  onClick={saveDefigoBuildingId}
                  disabled={isTogglingDefigo}
                  style={{ marginRight: -8 }}
                />
              }
              style={{ width: "300px", marginBottom: "10px" }}
            />
          </div>
          <div
            style={{
              display: "flex",
              gap: "var(--wlcm-spacing-xs)",
              flexWrap: "wrap",
            }}
          >
            <Button
              onClick={() => handleDefigoSync(true)}
              disabled={isRunningDefigoSync}
              isLoading={isRunningDefigoSync}
            >
              Run dry-run sync
            </Button>
            <Button
              onClick={() => handleDefigoSync(false)}
              disabled={isRunningDefigoSync}
              isLoading={isRunningDefigoSync}
            >
              Run real sync
            </Button>
          </div>
        </div>
        <TwoNIntercomCard
          key={JSON.stringify(building.twoN ?? null)}
          building={building}
          style={cardStyle}
          iconStyle={iconStyle}
        />
        <div style={cardStyle}>
          <h2>
            <Icon name="date_range" style={iconStyle} /> Welcome Booking
          </h2>
          <Toggle
            label="Booking enabled for building admins"
            checked={building.bookingEnabled ?? false}
            onChange={bookingEnabledHandler}
            disabled={isTogglingBooking}
          />
          <Toggle
            label="Booking enabled for all users"
            checked={building.bookingVisibleForUsers ?? false}
            onChange={bookingVisibleForUserHandler}
            disabled={isTogglingBookingVisibleForUsers}
          />
          {building.bookingEnabled && isBookingStatusLoading && (
            <>
              <hr
                style={{ margin: "var(--wlcm-spacing-sm) 0", opacity: 0.3 }}
              />
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  fontSize: "12px",
                  color: "var(--wlcm-color-text-secondary)",
                }}
              >
                <LoadingDots />
                Checking booking setup status…
              </div>
            </>
          )}
          {building.bookingEnabled && isBookingStatusError && (
            <>
              <hr
                style={{ margin: "var(--wlcm-spacing-sm) 0", opacity: 0.3 }}
              />
              <div
                style={{
                  display: "flex",
                  gap: "10px",
                  alignItems: "flex-start",
                  padding: "10px 12px",
                  background: "rgba(207, 58, 58, 0.10)",
                  border: "1px solid rgba(207, 58, 58, 0.35)",
                  borderRadius: "6px",
                }}
              >
                <Icon
                  name="warning"
                  style={{ color: "#a02525", fontSize: "18px", flexShrink: 0 }}
                />
                <div
                  style={{
                    fontSize: "12px",
                    color: "#6f1a1a",
                    lineHeight: 1.4,
                  }}
                >
                  Could not check booking setup status. Reload the page to try
                  again — do not change setup while status is unknown.
                </div>
              </div>
            </>
          )}
          {building.bookingEnabled &&
            isBookingStatusKnown &&
            isBookingConfigured && (
              <>
                <hr
                  style={{ margin: "var(--wlcm-spacing-sm) 0", opacity: 0.3 }}
                />
                <div
                  style={{
                    display: "flex",
                    gap: "12px",
                    alignItems: "flex-start",
                    padding: "12px 14px",
                    background: "rgba(96, 165, 97, 0.10)",
                    border: "1px solid rgba(96, 165, 97, 0.35)",
                    borderRadius: "8px",
                  }}
                >
                  <Icon
                    name="check_circle"
                    style={{
                      color: "#2e7d3b",
                      fontSize: "20px",
                      flexShrink: 0,
                      marginTop: "2px",
                    }}
                  />
                  <div
                    style={{
                      flex: 1,
                      display: "flex",
                      flexDirection: "column",
                      gap: "10px",
                    }}
                  >
                    <div>
                      <div
                        style={{
                          fontSize: "13px",
                          fontWeight: 600,
                          color: "#1f5c2a",
                          marginBottom: "2px",
                        }}
                      >
                        Booking setup complete
                      </div>
                      <div
                        style={{
                          fontSize: "12px",
                          color: "#1f5c2a",
                          lineHeight: 1.4,
                        }}
                      >
                        Booking setup is complete. You can now proceed with room
                        setup for booking in this building via Building settings
                        → Booking.
                      </div>
                    </div>
                    <Link
                      to="/building/settings/booking"
                      style={{
                        alignSelf: "flex-start",
                        textDecoration: "none",
                      }}
                    >
                      <Button>Go to booking settings</Button>
                    </Link>
                  </div>
                </div>
              </>
            )}
          {building.bookingEnabled &&
            isBookingStatusKnown &&
            !isBookingConfigured && (
              <>
                <hr
                  style={{ margin: "var(--wlcm-spacing-sm) 0", opacity: 0.3 }}
                />
                <div
                  style={{
                    display: "flex",
                    gap: "12px",
                    alignItems: "flex-start",
                    padding: "12px 14px",
                    background: "rgba(255, 176, 32, 0.10)",
                    border: "1px solid rgba(255, 176, 32, 0.35)",
                    borderRadius: "8px",
                  }}
                >
                  <Icon
                    name="info"
                    style={{
                      color: "#c07a00",
                      fontSize: "20px",
                      flexShrink: 0,
                      marginTop: "2px",
                    }}
                  />
                  <div
                    style={{
                      flex: 1,
                      display: "flex",
                      flexDirection: "column",
                      gap: "10px",
                    }}
                  >
                    <div>
                      <div
                        style={{
                          fontSize: "13px",
                          fontWeight: 600,
                          color: "#7a4d00",
                          marginBottom: "2px",
                        }}
                      >
                        Booking setup incomplete
                      </div>
                      <div
                        style={{
                          fontSize: "12px",
                          color: "#7a4d00",
                          lineHeight: 1.4,
                        }}
                      >
                        Complete the setup form to finish enabling booking.
                        Meeting rooms cannot be configured until this is done.
                      </div>
                    </div>
                    <Button
                      style={{ alignSelf: "flex-start" }}
                      onClick={() => setShowBookingSetupModal(true)}
                    >
                      Open setup form
                    </Button>
                  </div>
                </div>
              </>
            )}
          {building.bookingEnabled &&
            !isBookingConfigured &&
            showBookingSetupModal && (
              <PopupCard open onClose={() => setShowBookingSetupModal(false)}>
                <h3 style={{ marginTop: 0, marginBottom: "12px" }}>
                  Set up booking for this building
                </h3>
                <div
                  style={{
                    display: "flex",
                    gap: "10px",
                    alignItems: "flex-start",
                    padding: "10px 12px",
                    marginBottom: "20px",
                    background: "rgba(255, 176, 32, 0.12)",
                    border: "1px solid rgba(255, 176, 32, 0.35)",
                    borderRadius: "6px",
                  }}
                >
                  <Icon
                    name="info"
                    style={{
                      color: "#ffb020",
                      fontSize: "18px",
                      flexShrink: 0,
                    }}
                  />
                  <p
                    style={{
                      fontSize: "13px",
                      color: "rgba(255,255,255,0.9)",
                      margin: 0,
                      lineHeight: 1.4,
                    }}
                  >
                    Complete this form to finish enabling booking. Meeting rooms
                    cannot be configured until this is done.
                  </p>
                </div>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "16px",
                  }}
                >
                  {BOOKING_FIELD_GROUPS.map((group) => (
                    <div
                      key={group.section}
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "10px",
                      }}
                    >
                      <div
                        style={{
                          fontSize: "11px",
                          fontWeight: 600,
                          letterSpacing: "0.08em",
                          textTransform: "uppercase",
                          color: "rgba(255,255,255,0.55)",
                        }}
                      >
                        {group.section}
                      </div>
                      {group.fields.map(
                        ({
                          key,
                          label,
                          type,
                          readOnly,
                          placeholder,
                          help,
                          required,
                        }) => (
                          <div
                            key={key}
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              gap: "4px",
                            }}
                          >
                            <label
                              htmlFor={`bb-${key}`}
                              style={{
                                fontSize: "13px",
                                fontWeight: 500,
                                color: "rgba(255,255,255,0.9)",
                              }}
                            >
                              {label}
                              {required && (
                                <span
                                  aria-hidden
                                  style={{
                                    color: "#ff6b6b",
                                    marginLeft: "3px",
                                  }}
                                >
                                  *
                                </span>
                              )}
                            </label>
                            <input
                              id={`bb-${key}`}
                              aria-required={required}
                              type={type}
                              min={key === "floors" ? 1 : undefined}
                              step={key === "floors" ? 1 : undefined}
                              value={
                                bookingBuilding[
                                  key as keyof typeof bookingBuilding
                                ]
                              }
                              placeholder={placeholder}
                              disabled={
                                isCreatingBookingBuilding ||
                                bookingBuildingCreated
                              }
                              readOnly={readOnly}
                              ref={
                                key === firstEmptyEditableKey
                                  ? modalInitialFocusRef
                                  : undefined
                              }
                              onChange={(e) =>
                                updateBookingField(key, e.target.value)
                              }
                              style={{
                                width: "100%",
                                padding: "8px 12px",
                                fontSize: "14px",
                                border: "1px solid rgba(255,255,255,0.18)",
                                borderRadius: "6px",
                                background:
                                  readOnly || bookingBuildingCreated
                                    ? "rgba(255,255,255,0.06)"
                                    : "rgba(255,255,255,0.12)",
                                color:
                                  readOnly || bookingBuildingCreated
                                    ? "rgba(255,255,255,0.55)"
                                    : "#fff",
                                cursor:
                                  readOnly || bookingBuildingCreated
                                    ? "not-allowed"
                                    : "text",
                                outline: "none",
                              }}
                            />
                            {help && (
                              <span
                                style={{
                                  fontSize: "12px",
                                  color: "rgba(255,255,255,0.55)",
                                }}
                              >
                                {help}
                              </span>
                            )}
                          </div>
                        ),
                      )}
                    </div>
                  ))}
                </div>
                <div
                  style={{
                    display: "flex",
                    marginTop: "24px",
                    justifyContent: "flex-end",
                  }}
                >
                  <Button
                    disabled={
                      isCreatingBookingBuilding ||
                      bookingBuildingCreated ||
                      !isBookingSetupFormComplete(bookingBuilding)
                    }
                    isLoading={isCreatingBookingBuilding}
                    onClick={async () => {
                      if (!isBookingSetupFormComplete(bookingBuilding)) {
                        return;
                      }
                      setIsCreatingBookingBuilding(true);
                      try {
                        await createBookingBuilding({
                          buildingId: bookingBuilding.buildingId,
                          buildingName: bookingBuilding.buildingName.trim(),
                          description: bookingBuilding.description.trim(),
                          floors: Number(bookingBuilding.floors.trim()),
                          floorNames: bookingBuilding.floorNames
                            .split(",")
                            .map((f) => f.trim())
                            .filter(Boolean),
                          address: {
                            addressLines: [bookingBuilding.addressLines.trim()],
                            regionCode: bookingBuilding.regionCode,
                            postalCode: bookingBuilding.postalCode.trim(),
                            administrativeArea:
                              bookingBuilding.administrativeArea,
                            locality: bookingBuilding.locality.trim(),
                          },
                        });
                        setBookingBuildingCreated(true);
                        setShowBookingSetupModal(false);
                        await queryClient.invalidateQueries({
                          queryKey: [getBuildingId(), "bookingBuilding"],
                        });
                        alert("Booking building created successfully!");
                      } catch (error) {
                        console.error(error);
                        alert("Failed to create booking building");
                      } finally {
                        setIsCreatingBookingBuilding(false);
                      }
                    }}
                  >
                    {bookingBuildingCreated
                      ? "Saved ✓"
                      : "Create booking building"}
                  </Button>
                </div>
              </PopupCard>
            )}
        </div>
        <div style={cardStyle}>
          <h2>
            <Icon name="badge" style={iconStyle} /> Welcome Access
          </h2>
          <Toggle
            label="Access Premium enabled in building"
            checked={building.accessControlSystemIntegration ?? false}
            onChange={accessEnabledHandler}
            disabled={isTogglingAccess}
          />
          <Toggle
            label="Digital access cards enabled in building"
            checked={building.acsDigitalBadgeMode ?? false}
            onChange={buildingDigitalBadgeModeHandler}
            disabled={isTogglingBuildingDigitalBadgeMode}
          />
          <Toggle
            label="Allow users in the building to generate temporary access codes when they forget their card"
            checked={building.acsAllowForgotCardCodeGeneration ?? false}
            onChange={forgotCardCodeGenerationHandler}
            disabled={isTogglingForgotCardCodeGeneration}
          />
          <Toggle
            label="Allow building admins to set/reset PIN codes for users"
            checked={building.allowBuildingAdminPinManagement ?? false}
            onChange={allowBuildingAdminPinManagementHandler}
            disabled={isTogglingAllowBuildingAdminPinManagement}
          />
          <Toggle
            label="Allow access role management for building admins"
            checked={building.allowAccessRoleManagement ?? false}
            onChange={allowAccessRoleManagementHandler}
            disabled={isTogglingAllowAccessRoleManagement}
          />
          <hr style={{ margin: "var(--wlcm-spacing-sm) 0", opacity: 0.3 }} />
          <Toggle
            label="[Kill switch] Generate visitor access codes via legacy Poly (Sharry/Starwatch)"
            checked={building.acsVisitorAccessCodeViaPoly ?? false}
            onChange={visitorAccessCodeViaPolyHandler}
            disabled={isTogglingVisitorAccessCodeViaPoly}
          />
          <hr style={{ margin: "var(--wlcm-spacing-sm) 0", opacity: 0.3 }} />
          <Toggle
            label="[Feature flag] ACS Connectivity Guard enabled"
            checked={building.acsConnectivityGuardEnabled ?? false}
            onChange={acsConnectivityGuardHandler}
            disabled={isTogglingAcsConnectivityGuard}
          />
          <hr style={{ margin: "var(--wlcm-spacing-sm) 0", opacity: 0.3 }} />
          <h3 style={{ marginBottom: "var(--wlcm-spacing-xs)" }}>
            Access control connectivity
          </h3>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "var(--wlcm-spacing-xs)",
              marginBottom: "var(--wlcm-spacing-xs)",
            }}
          >
            <span
              style={{
                display: "inline-block",
                width: 10,
                height: 10,
                borderRadius: "50%",
                backgroundColor: building.acsConnectivityLost
                  ? "var(--wlcm-color-danger-red)"
                  : "var(--wlcm-color-success-green)",
                flexShrink: 0,
              }}
            />
            <span>
              {building.acsConnectivityLost ? "Connectivity lost" : "Connected"}
            </span>
          </div>
          <Toggle
            label="Mark connectivity as lost (blocks access operations)"
            checked={building.acsConnectivityLost ?? false}
            onChange={acsConnectivityHandler}
            disabled={isTogglingAcsConnectivityLost}
          />
          {showAcsMessageFields && (
            <>
              <p
                style={{
                  fontSize: "13px",
                  color: "var(--wlcm-color-text-secondary)",
                  margin: "var(--wlcm-spacing-xs) 0",
                }}
              >
                Custom banner message (leave blank for default)
              </p>
              <InputField
                compact
                label="Message (Norwegian)"
                placeholder="Tilkoblingen er nede..."
                value={acsConnectivityLostMessageNo}
                disabled={isTogglingAcsConnectivityLost}
                onChange={(e) =>
                  setAcsConnectivityLostMessageNo(e.target.value)
                }
              />
              <InputField
                compact
                label="Message (English)"
                placeholder="Connection is down..."
                value={acsConnectivityLostMessageEn}
                disabled={isTogglingAcsConnectivityLost}
                onChange={(e) =>
                  setAcsConnectivityLostMessageEn(e.target.value)
                }
                append={
                  <Button
                    circular
                    icon="save"
                    onClick={saveAcsConnectivityMessage}
                    disabled={isTogglingAcsConnectivityLost}
                    style={{ marginRight: -8 }}
                  />
                }
              />
            </>
          )}
          <hr style={{ margin: "var(--wlcm-spacing-sm) 0", opacity: 0.3 }} />
          <h3 style={{ marginBottom: "var(--wlcm-spacing-xs)" }}>
            Cards in ARX
          </h3>
          <p
            style={{
              fontSize: "13px",
              color: "var(--wlcm-color-text-secondary)",
              margin: "0 0 8px 0",
            }}
          >
            Sync runs across all buildings, not just this one.
          </p>
          <div
            style={{
              display: "flex",
              gap: "var(--wlcm-spacing-xs)",
              flexWrap: "wrap",
            }}
          >
            <Button
              onClick={() => handleCardsInArxSync(true)}
              disabled={isRunningCardsInArxSync}
              isLoading={isRunningCardsInArxSync}
            >
              Run dry-run sync
            </Button>
            <Button
              onClick={() => handleCardsInArxSync(false)}
              disabled={isRunningCardsInArxSync}
              isLoading={isRunningCardsInArxSync}
            >
              Run real sync
            </Button>
          </div>
          <hr style={{ margin: "var(--wlcm-spacing-sm) 0", opacity: 0.3 }} />
          <h3 style={{ marginBottom: "var(--wlcm-spacing-xs)" }}>
            Card migration
          </h3>
          <p
            style={{
              fontSize: "13px",
              color: "var(--wlcm-color-text-secondary)",
              margin: "0 0 8px 0",
            }}
          >
            Move existing plastic cards onto users in this building from a CSV
            or Excel file.
          </p>
          <Link
            to="/building/supersettings/card-migration"
            style={{ alignSelf: "flex-start", textDecoration: "none" }}
          >
            <Button>Open card migration</Button>
          </Link>
          {/* Same rule as the backend's isStarwatchGroupSyncBuilding */}
          {building.accessControlSystem === AccessSystem.STARWATCH &&
            building.starwatchIntegrationEnabled &&
            building.starwatch && (
              <>
                <hr
                  style={{ margin: "var(--wlcm-spacing-sm) 0", opacity: 0.3 }}
                />
                <h3 style={{ marginBottom: "var(--wlcm-spacing-xs)" }}>
                  Starwatch access groups
                </h3>
                <p
                  style={{
                    fontSize: "13px",
                    color: "var(--wlcm-color-text-secondary)",
                    margin: "0 0 8px 0",
                  }}
                >
                  Pull this building&apos;s access groups from Starwatch. Also
                  runs every hour.
                </p>
                <Button
                  onClick={handleStarwatchGroupSync}
                  disabled={isRunningStarwatchGroupSync}
                  isLoading={isRunningStarwatchGroupSync}
                  style={{ alignSelf: "flex-start" }}
                >
                  Sync Starwatch access groups
                </Button>
              </>
            )}
        </div>
        <div style={cardStyle}>
          <h2>
            <Icon name="dashboard" style={iconStyle} /> Dashboard Page
          </h2>
          <Toggle
            label="Dashboard enabled for building admins"
            checked={building.dashboardEnabledForAdmins ?? false}
            onChange={dashboardEnabledForAdminsHandler}
            disabled={isTogglingDashboardEnabledForAdmins}
          />
          <Toggle
            label="Dashboard enabled for all users"
            checked={building.dashboardEnabledForUsers ?? false}
            onChange={dashboardEnabledForUsersHandler}
            disabled={isTogglingDashboardEnabledForUsers}
          />
        </div>
        <div style={cardStyle}>
          <h2>
            <Icon name="print" style={iconStyle} /> Badge printing
          </h2>
          <Toggle
            label="Allow badge printing for building admins"
            checked={building.allowBadgePrinting ?? false}
            onChange={allowBadgePrintingHandler}
            disabled={isTogglingAllowBadgePrinting}
          />
        </div>
        <div style={cardStyle}>
          <h2>
            <Icon name="parking" style={iconStyle} /> Welcome Parking
          </h2>
          <Toggle
            label="Parking enabled for building"
            checked={building.hasParking ?? false}
            onChange={parkingHandler}
            disabled={isTogglingParking}
          />
        </div>
        <div style={cardStyle}>
          <h2>
            <Icon name="info" style={iconStyle} /> Handbook link
          </h2>
          <InputField
            value={handbookLink}
            disabled={isUpdatingHandbookLink}
            onChange={(e) => {
              setHandbooklink(e.target.value);
            }}
            append={
              <Button
                circular
                icon="save"
                isLoading={isUpdatingHandbookLink}
                disabled={isUpdatingHandbookLink}
                onClick={saveHandbookLink}
                style={{ marginRight: -8 }}
              />
            }
          />
        </div>

        <div style={cardStyle}>
          <h2>
            <Icon name="auto_delete" style={iconStyle} /> Quarterly user purge
          </h2>
          <Toggle
            label="Quarterly automatic user deletion enabled for building"
            checked={building.runQuarterlyUserPurge ?? false}
            onChange={quarterlyUserPurgeHandler}
            disabled={isTogglingQuarterlyUserPurge}
          />
          <Button
            size="sm"
            isLoading={isRunningQuarterlyUserPurgeManually}
            disabled={isRunningQuarterlyUserPurgeManually}
            onClick={async () => {
              try {
                setIsRunningQuarterlyUserPurgeManually(true);
                await runQuarterlyUserPurgeManually();
              } catch (error) {
                console.log("error ", error);
              } finally {
                setIsRunningQuarterlyUserPurgeManually(false);
              }
            }}
          >
            Run quarterly user purge manually
          </Button>
          <Button
            size="sm"
            isLoading={isRunningTwoWeekNotice}
            disabled={isRunningTwoWeekNotice}
            onClick={async () => {
              try {
                setIsRunningTwoWeekNotice(true);
                await runTwoWeekNoticeManually();
              } catch (error) {
                console.log("error ", error);
              } finally {
                setIsRunningTwoWeekNotice(false);
              }
            }}
          >
            Run two week notice manually
          </Button>
          <Button
            size="sm"
            isLoading={isRunningOneMonthNotice}
            disabled={isRunningOneMonthNotice}
            onClick={async () => {
              try {
                setIsRunningOneMonthNotice(true);
                await runOneMonthNoticeManually();
              } catch (error) {
                console.log("error ", error);
              } finally {
                setIsRunningOneMonthNotice(false);
              }
            }}
          >
            Run one month notice manually
          </Button>
        </div>

        <div style={cardStyle}>
          <form
            onSubmit={async (e) => {
              e.preventDefault();
              setIsCreatingNewBuilding(true);
              try {
                await createNewBuilding(
                  newBuildingId,
                  loggedInUser.email!,
                  selectedOwner?.value as string,
                );
              } catch (error) {
                console.log(error);
              } finally {
                queryClient.invalidateQueries({
                  queryKey: [getBuildingId(), "getAllBuildings"],
                });
                setIsCreatingNewBuilding(false);
              }
            }}
          >
            <h2>Create a new building</h2>
            <InputField
              placeholder="New building id"
              disabled={isCreatingNewBuilding}
              value={newBuildingId}
              onChange={(e) => setNewBuildingId(e.target.value.toLowerCase())}
              dangerText={
                newBuildingId && !/^[a-z0-9]+$/.test(newBuildingId)
                  ? "Only lowercase letters and numbers are allowed"
                  : undefined
              }
              required
            />
            <Dropdown
              compact
              options={buildingOwnerOptions}
              label={"Building owner"}
              value={selectedOwner}
              onChange={setSelectedOwner}
              required
            />
            <Button
              type="submit"
              isLoading={isCreatingNewBuilding}
              disabled={
                isCreatingNewBuilding ||
                !newBuildingId ||
                !/^[a-z0-9]+$/.test(newBuildingId)
              }
            >
              Create building
            </Button>
          </form>
        </div>
        <div style={cardStyle}>
          {allBuildings.map((b: string) => (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                paddingBottom: "8px",
                borderBottom: "1px solid rgba(0,0,0,0.1)",
              }}
              key={b}
            >
              {b}{" "}
              <Button
                size="sm"
                secondary
                isLoading={isMakingUserBuildingAdmin}
                disabled={isMakingUserBuildingAdmin}
                style={{ marginLeft: "10px" }}
                onClick={async () => {
                  try {
                    setIsMakingUserBuildingAdmin(true);
                    await makeUserBuildingAdmin(loggedInUser.userId, b);
                  } catch (error) {
                    console.log("error ", error);
                  } finally {
                    setIsMakingUserBuildingAdmin(false);
                  }
                }}
              >
                Add me to building
              </Button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
